import { useEffect, useState, useCallback } from 'react';
import Fetch from '../services/fetchService';
import Post from '../services/postService';
import AuthData from '../common/AuthData';
import { ORDERS_API, SEND_EMAIL_API } from '../common/ResourcePaths';
import RelativeSvgLoader from '../common/components/RelativeSvgLoader';
import DataLine from '../common/components/DataLine'; // Adjust the path if necessary
import SingleTile from '../common/components/SingleTitle'; // Adjust the import path as needed
import YesNoPopup from '../common/components/YesNoPopup';
import RefreshButton from '../common/components/RefreshButton';
import { CANCELLED, PROCESSING, NEW, DELIVERING, COMPLETED } from '../common/OrderStatus';
import { IoMdArrowBack, IoMdCreate, IoMdCheckmark, IoMdClose } from 'react-icons/io';
import Error from  '../common/components/ErrorMessage'
import Feedback from '../common/components/Feedback';

// Component for editable data fields - moved outside to prevent re-creation
const EditableDataLine = ({ label, value, field, type = "text", onChange }) => {
    return (
        <>
            <label htmlFor={field} className="form-label">
                {label}:
            </label>
            <input
                type={type}
                id={field}
                name={field}
                value={value}
                onChange={(e) => onChange(field, e.target.value)}
                className="input-field"
            />
        </>
    );
}

const OrderView = ({ id, closeOrderView }) => {
    
    const [orderDetails, setOrderDetails] = useState(null);
    const [error, setError] = useState('');
    const [showPopup, setShowPopup] = useState(false); // State for popup visibility
    const [loader, setLoader] = useState(true); // State for loader visibility
    const [actualStatus, setActualStatus] = useState(NEW); // State to hold the actual status to be set
    const [isEditMode, setIsEditMode] = useState(false); // State for edit mode
    const [editableData, setEditableData] = useState({}); // State for editable delivery data

    const fetchOrderDetails = useCallback(async () => {
        try {
            setLoader(true);
            setError(''); // Clear any previous errors
            const authData = AuthData.getAuthData();
            const url = `${authData.appFunctionUrl}${ORDERS_API}/${id}?code=${authData.azureFunctionKey}`;
            const response = await Fetch(url, authData.clientId);
            setOrderDetails(response.data);
            // Initialize editable data with current order details
            setEditableData({
                fullName: response.data.fullName || '',
                address: response.data.address || '',
                city: response.data.city || '',
                postalCode: response.data.postalCode || '',
                email: response.data.email || '',
                phone: response.data.phone || ''
            });
        } catch (error) {
            setError("Failed to fetch order details. Please try again later.");
        } finally {
            setLoader(false); // Hide loader after fetching data
        }
    }, [id]);

    const handleRefresh = () => {
        fetchOrderDetails();
    };

    useEffect(() => {
        fetchOrderDetails();
    }, [id, fetchOrderDetails]);

    const handleYes = async () => {
        setLoader(true); // Show loader while saving data
        orderDetails.status = actualStatus; // Set the status to the actual status
        setShowPopup(false);
        const authData = AuthData.getAuthData();

        const url = `${authData.appFunctionUrl}${ORDERS_API}?code=${authData.azureFunctionKey}`; // Use the constant here

        try {
            const response = await Post(url,authData.clientId, orderDetails); // Assuming `save` is the method in postService
            setOrderDetails(response.data); // Update order details with the response data

            // Send email notification when status changes to Processing or In Transit
            if (actualStatus === PROCESSING || actualStatus === DELIVERING) {
                try {
                    const emailUrl = `${authData.appFunctionUrl}${SEND_EMAIL_API}?code=${authData.azureFunctionKey}`;
                    // Format data as CheckoutData structure expected by the email service
                    const checkoutData = {
                        clientId: authData.clientId,
                        number: response.data.number,
                        companyName: "", // Not available in order details
                        fullName: response.data.fullName,
                        address: response.data.address,
                        city: response.data.city, // City is mandatory and always present
                        phone: response.data.phone,
                        deliveryTime: "", // Not available in order details
                        paymentMethod: response.data.paymentMethod,
                        email: response.data.email,
                        notes: response.data.notes,
                        orderItems: response.data.orderItems || [],
                        finalPrice: response.data.finalPrice || 0,
                        promoCode: "", // Not available in order details
                        deliveryOptionId: "", // Not available in order details
                        status: actualStatus
                    };
                    
                    await Post(emailUrl, authData.clientId, checkoutData);
                } catch (emailError) {
                    console.error('Error sending status change email:', emailError);
                    // Note: We don't show error to user as the main operation (status update) succeeded
                }
            }

        } catch (error) {
            console.error('Error saving data:', error);
            setError("An error occurred while saving data.");
        }finally{
            setLoader(false);
            closeOrderView(actualStatus, true); // Close the order view after saving with filter change
        }
    };

    const handleNo = () => {
        setShowPopup(false); // Close popup on "No"
    };

    const changeStatus = (status) => {
        if(status){
            setActualStatus(status); 
        }else{
            let nextStatus = getNextStatus();
            setActualStatus(nextStatus); // Set the next status based on current status
        }
        setShowPopup(true); // Show popup when setting status
    }

    const getNextStatus = () => {
        switch (orderDetails.status) {
            case NEW:
                return PROCESSING;
            case PROCESSING:
                return DELIVERING;
            case DELIVERING:
                return COMPLETED;
            default:
                return "Unknown Status";
        }
    }

    const getButtonName = () => {
        switch (orderDetails.status) {
            case NEW:
                return "Processing";
            case PROCESSING:
                return "Delivering";
            case DELIVERING:
                return "Completed";
            default:
                return "Unknown Status";
        }
    }

    const buttonsVisible = () => {
        return orderDetails && orderDetails.status !== COMPLETED && orderDetails.status !== CANCELLED;
    }

    const editModeAllowed = () => {
        return orderDetails && orderDetails.status !== COMPLETED && orderDetails.status !== CANCELLED;
    }

    const handleEditClick = () => {
        setIsEditMode(true);
    }

    const handleCancelEdit = () => {
        setIsEditMode(false);
        // Reset editable data to original values
        setEditableData({
            fullName: orderDetails.fullName || '',
            address: orderDetails.address || '',
            city: orderDetails.city || '',
            postalCode: orderDetails.postalCode || '',
            email: orderDetails.email || '',
            phone: orderDetails.phone || ''
        });
    }

    const handleInputChange = (field, value) => {
        setEditableData(prev => ({
            ...prev,
            [field]: value
        }));
    }

    const handleSaveDeliveryData = async () => {
        try {
            setLoader(true);
            const authData = AuthData.getAuthData();
            
            // Update order details with edited data
            const updatedOrderDetails = {
                ...orderDetails,
                fullName: editableData.fullName,
                address: editableData.address,
                city: editableData.city,
                postalCode: editableData.postalCode,
                email: editableData.email,
                phone: editableData.phone
            };

            const url = `${authData.appFunctionUrl}${ORDERS_API}?code=${authData.azureFunctionKey}`;
            const response = await Post(url, authData.clientId, updatedOrderDetails);
            
            setOrderDetails(response.data);
            setIsEditMode(false);
            
        } catch (error) {
            console.error('Error saving delivery data:', error);
            setError("An error occurred while saving delivery data.");
        } finally {
            setLoader(false);
        }
    }

    return (
        <div className="container" style={{ position: 'relative' }}>
                {loader && <RelativeSvgLoader />}
                {error && <Error message={error}/>} {/* Render error message */}
                <div className="form-wrapper" style={{ opacity: loader ? 0.3 : 1 }}>
                    {/* Back to Orders button */}
                    <div style={{ 
                        marginBottom: '20px', 
                    paddingBottom: '15px', 
                    borderBottom: '2px solid #e9ecef',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {!isEditMode && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => closeOrderView(orderDetails?.status, true)}
                                    disabled={loader}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid #ccc',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: loader ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                    }}
                                    onMouseOver={(e) => {
                                        if (!loader) {
                                            e.currentTarget.style.borderColor = '#888';
                                            e.currentTarget.style.color = '#333';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (!loader) {
                                            e.currentTarget.style.borderColor = '#ccc';
                                            e.currentTarget.style.color = '#666';
                                        }
                                    }}
                                    title="Back to Orders"
                                >
                                    <IoMdArrowBack size={20} />
                                </button>
                                
                                <RefreshButton onClick={handleRefresh} loader={loader} />
                            </>
                        )}
                        
                        {/* Edit delivery data button */}
                        {editModeAllowed() && !isEditMode && (
                            <button
                                type="button"
                                onClick={handleEditClick}
                                disabled={loader}
                                style={{
                                    background: 'transparent',
                                    border: '1px solid #ccc',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: loader ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    color: '#666'
                                }}
                                onMouseOver={(e) => {
                                    if (!loader) {
                                        e.currentTarget.style.borderColor = '#888';
                                        e.currentTarget.style.color = '#333';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (!loader) {
                                        e.currentTarget.style.borderColor = '#ccc';
                                        e.currentTarget.style.color = '#666';
                                    }
                                }}
                                title="Edit delivery information"
                            >
                                <IoMdCreate size={20} />
                            </button>
                        )}
                        
                        {/* Save/Cancel buttons for edit mode */}
                        {isEditMode && (
                            <>
                                <button
                                    type="button"
                                    onClick={handleSaveDeliveryData}
                                    disabled={loader}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid #10b981',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: loader ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        color: '#10b981'
                                    }}
                                    onMouseOver={(e) => {
                                        if (!loader) {
                                            e.currentTarget.style.borderColor = '#059669';
                                            e.currentTarget.style.color = '#059669';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (!loader) {
                                            e.currentTarget.style.borderColor = '#10b981';
                                            e.currentTarget.style.color = '#10b981';
                                        }
                                    }}
                                    title="Save changes"
                                >
                                    <IoMdCheckmark size={20} />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    disabled={loader}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid #ef4444',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: loader ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        color: '#ef4444'
                                    }}
                                    onMouseOver={(e) => {
                                        if (!loader) {
                                            e.currentTarget.style.borderColor = '#dc2626';
                                            e.currentTarget.style.color = '#dc2626';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (!loader) {
                                            e.currentTarget.style.borderColor = '#ef4444';
                                            e.currentTarget.style.color = '#ef4444';
                                        }
                                    }}
                                    title="Cancel editing"
                                >
                                    <IoMdClose size={20} />
                                </button>
                            </>
                        )}
                    </div>
                    
                </div>
                <form>
                    <div className="form-grid">
                        <DataLine label="Order ID" data={orderDetails?.id} />
                        <DataLine label="Order number" data={orderDetails?.number} />
                        
                        {/* Editable delivery fields */}
                        {isEditMode ? (
                            <>
                                <EditableDataLine 
                                    label="Full Name" 
                                    value={editableData.fullName} 
                                    field="fullName"
                                    onChange={handleInputChange}
                                />
                                <EditableDataLine 
                                    label="Address" 
                                    value={editableData.address} 
                                    field="address"
                                    onChange={handleInputChange}
                                />
                                <EditableDataLine 
                                    label="City" 
                                    value={editableData.city} 
                                    field="city"
                                    onChange={handleInputChange}
                                />
                                <EditableDataLine 
                                    label="Postal Code" 
                                    value={editableData.postalCode} 
                                    field="postalCode"
                                    onChange={handleInputChange}
                                />
                                <EditableDataLine 
                                    label="Email" 
                                    value={editableData.email} 
                                    field="email" 
                                    type="email"
                                    onChange={handleInputChange}
                                />
                                <EditableDataLine 
                                    label="Phone" 
                                    value={editableData.phone} 
                                    field="phone" 
                                    type="tel"
                                    onChange={handleInputChange}
                                />
                            </>
                        ) : (
                            <>
                                <DataLine label="Full Name" data={orderDetails?.fullName} />
                                <DataLine label="Address" data={orderDetails?.address} />
                                <DataLine label="City" data={orderDetails?.city} />
                                <DataLine label="Postal Code" data={orderDetails?.postalCode} />
                                <DataLine label="Email" data={orderDetails?.email} />
                                <DataLine label="Phone" data={orderDetails?.phone} />
                            </>
                        )}
                        
                        <DataLine label="Payment Method" data={orderDetails?.paymentMethod} />
                        <DataLine label="Original Price" data={`$${orderDetails?.originalPrice}`} />
                        <DataLine label="Final Price" data={`$${orderDetails?.finalPrice}`} />
                        <DataLine label="Notes" data={orderDetails?.notes} />
                    </div>
                    {orderDetails?.orderItems && orderDetails.orderItems.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap',marginTop: '10px', gap: '12px' }}>
                                {orderDetails.orderItems.map((item, index) => (
                                    <SingleTile
                                        key={index}
                                        name={item.text}
                                        value={item.count}
                                    />
                                ))}
                            </div>
                        ) : (
                            <DataLine label="Order Items" data="No items available for this order." id="no-items" />
                        )}
                </form>
                {buttonsVisible() && (<div className='form-footer'>
                            <button 
                                type="submit" 
                                onClick={() => changeStatus()} 
                                className="submit-button"
                                disabled={loader}
                            >
                                {getButtonName()}
                            </button>
                            <button 
                                type="button" 
                                className='clear-data-button' 
                                style={{ marginLeft: '10px' }} 
                                onClick={() => changeStatus(CANCELLED)}
                                disabled={loader}
                            >
                                Cancel
                            </button>
                </div>)}
            </div>
            {showPopup && (
               <YesNoPopup 
                message='Are you sure you want to change the status of this order? This action cannot be undone.'
                handleYes={handleYes} 
                handleNo={handleNo}/>
            )}
            
            {/* Feedback system available on order details page */}
            <Feedback />
        </div>
    );
};

export default OrderView;